import tinycolor from "tinycolor2"
import React, {Component} from "react"
import {Row} from "@irati/ui/src/Layout"
import {Team, Branding, TeamState} from "@irati/types"
import {injectIntl, InjectIntl, FormattedMessage} from "@irati/utils/lang"
import {uploadImage} from "@irati/images/services/image"
import authentication from "@irati/auth"
import adminTracking from "@irati-tracking/admin"
import id from "@irati/utils/id"
import brandingService from "@irati/brand/services/branding"
import Loading from "@irati/ui/src/Loading"
import mainTheme from "@irati/ui/src/global/theme"
import {H1} from "@irati/ui/src/Typography"
import {Select} from "@irati/ui/src/FormItem"
import Flex from "@irati/ui/src/Flex"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import teamService from "@irati/identity/services/teams"
import Button from "@irati/ui/src/Button"
import teamNameHelper from "@irati/identity/utils/team/teamName"
import {getAllTeams} from "@irati/identity/helpers/teams/getAllTeamsForTenant"

import UploadLogoStep from "./UploadLogoStep"
import ScaleLogoStep from "./ScaleLogoStep"
import PickColorStep from "./PickColorStep"
import BrandSettings from "./BrandSettings"

const SELECT_LOGO_OR_COLOR = "select_logo_or_color"
const UPLOAD_LOGO = "upload_logo"
const SCALE_LOGO = "scale_logo"
const PICK_COLOR = "pick_color"
interface Props extends InjectIntl {
  onBrandUpdated: () => void
  onColorUpdate: () => void
  imageLink: string
  showUpload: boolean
}
interface State {
  teams: Team[]
  brandedOptions: Option[]
  unbrandedOptions: Option[]
  logoFile: File | null
  logoContents: null
  currentStep: string
  colorsList: boolean
  showSkip: boolean
  selectedTeamUuid: string
  color: string | null
  logo: string | null
  saving: Promise<void> | null
  rootTeamBranding: Branding
}
class UpdateBrand extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      logoFile: null,
      logoContents: null,
      currentStep: props.showUpload ? UPLOAD_LOGO : SELECT_LOGO_OR_COLOR,
      colorsList: false,
      showSkip: true,
      selectedTeamUuid: authentication.business().uuid,
      color: null,
      logo: null,
      saving: null,
      brandedOptions: [],
      unbrandedOptions: []
    }
  }
  componentDidMount() {
    if (this.props.imageLink) {
      const that = this
      const img = document.createElement("img")
      img.crossOrigin = "Anonymous"
      img.onload = function () {
        const colorThief = new window.ColorThief()
        that.setState({
          colorsList: colorThief.getPalette(img, 2)
        })
      }
      img.src = this.props.imageLink
    }
    this.getTeamsAndPermissions()
  }
  setRootTeamBranding = () => {
    const {businessLogoUrl, primaryColour} = this.state.rootTeamBranding
    this.setState({selectedTeamUuid: authentication.business().uuid, color: primaryColour, logo: businessLogoUrl})
  }
  getTeamsAndPermissions = () => {
    const {formatMessage} = this.props
    const business = authentication.business()

    const load = officesForTenant().then(async (protectedTeams) => {
      const rootTeam = await teamService
        .teamsPaginated(business.uuid, undefined, undefined, undefined, TeamState.Protected)
        .then(({teams}) => teams.find((t) => t.teamName === "$$TEAM_ROOT$$") as Team)

      return getAllTeams().then((teams) => {
        const brandedOptions = [
          {label: business.businessName, value: business.uuid},
          ...protectedTeams
            .filter((team) => team.branding.isEnabled)
            .map((team) => ({label: formatMessage({id: teamNameHelper(team)}), value: team.uuid})),
          ...teams.filter((team) => team.branding.isEnabled).map((team) => ({label: teamNameHelper(team), value: team.uuid}))
        ]

        const unbrandedOptions = [
          ...protectedTeams.filter((team) => !team.branding.isEnabled).map((team) => ({label: teamNameHelper(team), value: team.uuid})),
          ...teams.filter((team) => !team.branding.isEnabled).map((team) => ({label: teamNameHelper(team), value: team.uuid}))
        ]

        this.setState(
          {
            teams: [...protectedTeams, ...teams],
            brandedOptions,
            unbrandedOptions,
            rootTeamBranding: rootTeam?.branding
          },
          this.setRootTeamBranding
        )
      })
    })
    this.setState({saving: load})
  }
  onLogoUploaded = (file, contents) => {
    this.setState({
      logoFile: file,
      logoContents: contents,
      currentStep: SCALE_LOGO
    })
    adminTracking.updateLogo()
  }
  onLogoScaled = (resizedImage) => {
    const colorThief = new window.ColorThief()
    const colorsList = colorThief.getPalette(resizedImage, 2)
    const base64Image = resizedImage.toDataURL()
    const saving = uploadImage(base64Image).then((imageLink) => {
      const params = {
        businessId: authentication.business().uuid,
        teamId: this.getTeamId(),
        businessLogoUrl: imageLink,
        isEnabled: true
      }
      return brandingService.update(params).then(() => {
        this.setState({
          colorsList,
          currentStep: PICK_COLOR,
          showSkip: true
        })
      })
    })
    this.setState({saving})
  }
  getTeamId = () => {
    const {selectedTeamUuid} = this.state
    if (selectedTeamUuid === authentication.business().uuid) {
      return authentication.business().rootTeamId
    }
    return selectedTeamUuid
  }
  onColorSelected = (color: string) => {
    const business = authentication.business()
    const data = {
      businessId: business.uuid,
      teamId: this.getTeamId(),
      primaryColour: color,
      textColour: tinycolor(color).isLight() ? mainTheme.colors.utility.ink : mainTheme.colors.utility.white,
      isEnabled: true
    }
    const saving = brandingService.update(data).then(() => {
      this.props.onBrandUpdated()
      if (this.props.onColorUpdate) {
        this.props.onColorUpdate(color)
      }
    })
    this.setState({saving})
  }
  handleChange = (selectedTeamUuid: string) => {
    if (selectedTeamUuid === authentication.business().uuid) {
      return this.setRootTeamBranding()
    }
    const {teams} = this.state
    const selectedTeam = teams.find(({uuid}) => uuid === selectedTeamUuid)
    const color = selectedTeam.branding.primaryColour
    this.setState({selectedTeamUuid, color, logo: selectedTeam.branding.businessLogoUrl})
  }

  updateCurrentStepToUploadLogo = () => {
    this.setState({
      currentStep: UPLOAD_LOGO,
      logoFile: null,
      logoContents: null
    })
  }
  handleBrandSettingsClickColor = () => {
    this.setState({
      currentStep: PICK_COLOR,
      showSkip: false
    })
  }

  handleBrandingEnabledUpdate = (isEnabled: boolean) => {
    const business = authentication.business()
    const data = {
      businessId: business.uuid,
      teamId: this.getTeamId(),
      isEnabled
    }
    const saving = brandingService.update(data).then(() => {
      this.props.onBrandUpdated()
    })
    this.setState({saving})
  }

  handleBackToSelect = () => {
    this.setState({currentStep: SELECT_LOGO_OR_COLOR})
  }

  getStep = () => {
    const {currentStep, colorsList, logoContents, showSkip, selectedTeamUuid, color, logo, teams, brandedOptions, unbrandedOptions} =
      this.state
    const {formatMessage} = this.props
    const isCurrentOptionEnabled = !!brandedOptions.find(({value}) => value === selectedTeamUuid)

    switch (currentStep) {
      case SELECT_LOGO_OR_COLOR:
        return (
          <>
            <H1 marginBottom={2}>
              <FormattedMessage id="admin.components.brandSettings.headerTwo" />
            </H1>
            {teams?.length > 0 && (
              <>
                <Row>
                  <Flex direction="column" maxWidth="360px">
                    <Select
                      label={formatMessage({id: "admin.components.brandSettings.selectBranding"})}
                      options={brandedOptions}
                      name="selectBranding"
                      value={selectedTeamUuid}
                      onChange={this.handleChange}
                    />
                  </Flex>
                </Row>
                {unbrandedOptions?.length > 0 && (
                  <Row>
                    <Flex direction="column" maxWidth="360px">
                      <Select
                        label={formatMessage({id: "admin.components.brandSettings.enableBranding2"})}
                        options={unbrandedOptions}
                        name="enableBranding"
                        value={selectedTeamUuid}
                        onChange={this.handleChange}
                      />
                    </Flex>
                  </Row>
                )}
                {isCurrentOptionEnabled && selectedTeamUuid !== authentication.business().uuid && (
                  <Button id="disableBranding_button" primary marginBottom={2} onClick={() => this.handleBrandingEnabledUpdate(false)}>
                    {formatMessage({id: "admin.components.brandSettings.button.disable"})}
                  </Button>
                )}
                {!isCurrentOptionEnabled && (
                  <Button id="enableBranding_button" primary marginBottom={2} onClick={() => this.handleBrandingEnabledUpdate(true)}>
                    {formatMessage({id: "admin.components.brandSettings.button.enable"})}
                  </Button>
                )}
              </>
            )}
            <BrandSettings
              logo={logo}
              primaryColour={color}
              handleBrandSettingsClickLogo={this.updateCurrentStepToUploadLogo}
              handleBrandSettingsClickColor={this.handleBrandSettingsClickColor}
            />
          </>
        )
      case UPLOAD_LOGO:
        return <UploadLogoStep onComplete={this.onLogoUploaded} handleBackToSelect={this.handleBackToSelect} />
      case SCALE_LOGO:
        return (
          <ScaleLogoStep imageSource={logoContents} backToUploadLogo={this.updateCurrentStepToUploadLogo} onComplete={this.onLogoScaled} />
        )
      case PICK_COLOR:
        return (
          <PickColorStep
            id={id("pick-colors", this.props)}
            colorsList={colorsList}
            onColorSelected={this.onColorSelected}
            onSkip={this.props.onBrandUpdated}
            showSkip={showSkip}
            handleBackToSelect={this.handleBackToSelect}
          />
        )
      default:
        return <div>UNKNOWN STEP</div>
    }
  }
  render() {
    const {saving} = this.state
    const idNamespace = id("update-brand-modal", this.props)
    return (
      <Loading id={idNamespace} load={saving}>
        {this.getStep()}
      </Loading>
    )
  }
}
export default injectIntl(UpdateBrand)
